import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CategoryBadge from './CategoryBadge'
import { TOOLS, type ToolMeta } from '../config/tools'
import { getRecentTools, addRecentTool } from '../hooks/useRecentTools'

// ── CommandPalette ─────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  open:          boolean
  onClose:       () => void
  initialQuery?: string
}

function filterTools(query: string): ToolMeta[] {
  if (!query.trim()) {
    // Show recently used
    const recentIds = getRecentTools()
    if (recentIds.length > 0) {
      return recentIds.map(id => TOOLS.find(t => t.id === id)).filter(Boolean) as ToolMeta[]
    }
    return TOOLS.slice(0, 10)
  }
  const q = query.toLowerCase()
  return TOOLS.filter(t =>
    t.name.toLowerCase().includes(q) ||
    t.category.toLowerCase().includes(q) ||
    t.description.toLowerCase().includes(q),
  )
}

export default function CommandPalette({ open, onClose, initialQuery }: CommandPaletteProps) {
  const navigate     = useNavigate()
  const [query, setQuery]       = useState('')
  const [selected, setSelected] = useState(0)
  const inputRef  = useRef<HTMLInputElement>(null)
  const listRef   = useRef<HTMLUListElement>(null)

  const results = useMemo(() => filterTools(query), [query])

  // Reset state when opened/closed
  useEffect(() => {
    if (open) {
      setQuery(initialQuery ?? '')
      setSelected(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open, initialQuery])

  // Keep selected in bounds
  useEffect(() => {
    if (selected >= results.length) setSelected(Math.max(0, results.length - 1))
  }, [results, selected])

  // Scroll selected item into view
  useEffect(() => {
    const item = listRef.current?.children[selected] as HTMLElement | undefined
    item?.scrollIntoView({ block: 'nearest' })
  }, [selected])

  const navigateTo = useCallback((tool: ToolMeta) => {
    addRecentTool(tool.id)
    navigate(tool.route)
    onClose()
  }, [navigate, onClose])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelected(s => Math.min(s + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected(s => Math.max(s - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const tool = results[selected]
      if (tool) navigateTo(tool)
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        role="dialog"
        aria-label="Command palette"
        aria-modal="true"
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
      >
        <div className="w-full max-w-lg rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl shadow-black/60 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-neutral-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setSelected(0) }}
              onKeyDown={handleKeyDown}
              placeholder="Search tools…"
              className="flex-1 bg-transparent text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none"
              aria-label="Search tools"
              aria-autocomplete="list"
            />
            <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono text-neutral-500 bg-neutral-800 border border-neutral-700 rounded">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[360px] overflow-y-auto">
            {!query && getRecentTools().length > 0 && (
              <p className="px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-neutral-600">
                Recently Used
              </p>
            )}
            {results.length === 0 ? (
              <p className="px-4 py-6 text-sm text-neutral-500 italic text-center">
                No tools found for "{query}"
              </p>
            ) : (
              <ul ref={listRef} role="listbox" aria-label="Tool results">
                {results.map((tool, i) => (
                  <li
                    key={tool.id}
                    role="option"
                    aria-selected={i === selected}
                    onClick={() => navigateTo(tool)}
                    onMouseEnter={() => setSelected(i)}
                    className={[
                      'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
                      i === selected ? 'bg-neutral-800' : 'hover:bg-neutral-800/60',
                    ].join(' ')}
                  >
                    <CategoryBadge category={tool.category} size="xs" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-100 truncate">{tool.name}</p>
                      <p className="text-xs text-neutral-500 truncate">{tool.description}</p>
                    </div>
                    {i === selected && (
                      <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono text-neutral-500 bg-neutral-900 border border-neutral-700 rounded">
                        ↵
                      </kbd>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-neutral-800 flex items-center gap-3 text-[10px] text-neutral-600">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> open</span>
            <span><kbd className="font-mono">Esc</kbd> close</span>
          </div>
        </div>
      </div>
    </>
  )
}
